/**
 * Frontend mirror of backend/src/services/entitlements.ts — kept deliberately
 * tiny and duplicated (not shared via a monorepo package) so the frontend can
 * compute "is this nav item locked for the current plan" without an extra
 * round-trip. The backend is always the source of truth for enforcement —
 * this is purely a UI hint (nav badges, disabling buttons pre-emptively).
 * See docs/BUSINESS_MODEL.md section 2 for the tier design.
 */
import type { Tenant } from '@/lib/api/v1';

export type PlanId = 'free' | 'pluss' | 'pro' | 'legacy';

const KNOWN_PLANS: readonly PlanId[] = ['free', 'pluss', 'pro', 'legacy'];

export interface EntitlementCapabilities {
  maxBottles: number | null;
  maxHouseholdMembers: number | null;
  labelPrint: boolean;
  wineListGenerator: boolean;
  stockCount: boolean;
  priceCalculator: boolean;
  orders: boolean;
  statsExport: boolean;
  posFeed: boolean;
  /** Public restaurant/tenant profile page at /p/:profileSlug — see routes/profil.tsx. */
  publicProfile: boolean;
}

export type CapabilityKey = Exclude<keyof EntitlementCapabilities, 'maxBottles' | 'maxHouseholdMembers'>;

const FREE: EntitlementCapabilities = {
  maxBottles: 50,
  maxHouseholdMembers: 2,
  labelPrint: false,
  wineListGenerator: false,
  stockCount: false,
  priceCalculator: false,
  orders: false,
  statsExport: false,
  posFeed: false,
  publicProfile: false,
};

const PLUSS: EntitlementCapabilities = {
  maxBottles: null,
  maxHouseholdMembers: null,
  labelPrint: true,
  wineListGenerator: false,
  stockCount: false,
  priceCalculator: false,
  orders: false,
  statsExport: true,
  posFeed: false,
  publicProfile: false,
};

const PRO: EntitlementCapabilities = {
  maxBottles: null,
  maxHouseholdMembers: null,
  labelPrint: true,
  wineListGenerator: true,
  stockCount: true,
  priceCalculator: true,
  orders: true,
  statsExport: true,
  posFeed: true,
  publicProfile: true,
};

const LEGACY: EntitlementCapabilities = { ...PRO };

const PLAN_CAPABILITIES: Record<PlanId, EntitlementCapabilities> = {
  free: FREE,
  pluss: PLUSS,
  pro: PRO,
  legacy: LEGACY,
};

export function resolvePlan(plan: string | null | undefined): PlanId {
  return (KNOWN_PLANS as readonly string[]).includes(String(plan)) ? (plan as PlanId) : 'free';
}

export function resolveEntitlements(tenant: Pick<Tenant, 'plan'> & { entitlementsOverride?: Record<string, unknown> | null }): EntitlementCapabilities {
  const base = PLAN_CAPABILITIES[resolvePlan(tenant?.plan)];
  const override = tenant?.entitlementsOverride;
  if (!override || typeof override !== 'object' || Array.isArray(override)) return base;
  return { ...base, ...(override as Partial<EntitlementCapabilities>) };
}

export function planLabel(plan: PlanId | string | null | undefined): string {
  switch (resolvePlan(plan)) {
    case 'free':
      return 'Gratis';
    case 'pluss':
      return 'Pluss';
    case 'pro':
      return 'Pro';
    case 'legacy':
      return 'Legacy';
  }
}

/** The minimum plan tier that unlocks a capability — used for nav badges ("Pro"). */
export function minPlanFor(capability: CapabilityKey): 'pluss' | 'pro' {
  return PLUSS[capability] ? 'pluss' : 'pro';
}

/** Nav route -> capability it requires, for AppShell's locked-badge hint. Routes not listed are available on every plan. */
export const NAV_CAPABILITY_BY_PATH: Partial<Record<string, CapabilityKey>> = {
  '/vinliste': 'wineListGenerator',
  '/varetelling': 'stockCount',
  '/prising': 'priceCalculator',
  '/bestilling': 'orders',
  '/labels': 'labelPrint',
  '/pos': 'posFeed',
  '/profil': 'publicProfile',
};
